// Importing necessary modules
import React from 'react';
import { Fragment, useState, useRef, useContext, useEffect } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import MultiSelectInput from "../../Shared/MultiSelect/MultiSelect";
import { MsalContext } from '@azure/msal-react';
import { sendEmail } from "../../../API/EmailApi";
import InnerLoader from '../../Shared/Loader/InnerLoader';
import { OverlayLoader } from '../../Shared/Loader/OverlayLoader';
import { getAzureUser } from '../../../API/User/User';
import { useTranslation } from 'react-i18next';
import { changeUserOrganizationRole, inviteUserToOrganization } from '../../../API/Organization/organization';

// Component for inviting a user to an organization
export const InviteUsertoOrganizationForm = ({
    organization,
    orgRole,
    organizationUsers,
    showInIframe,
    toUpdateUser,
    closeDialog,
    updateUsers,
    showNotification
}) => {

    // Get the context object from the MsalContext
    const context = useContext(MsalContext);
    const [userAzureData, setUserAzureData] = useState({});
    const { t, i18n } = useTranslation([
        "users/invite_user_dialog"
    ], {
        useSuspense: false
    }) // Translation

    const getRoleData = (data) => {
        for (let role of data) {
            const key = role.roleType.toLowerCase().replace(' ', '_');
            const roleData = t("roles." + key, { returnObjects: true });
            role.roleTypeText = roleData.type
            role.roleDescriptionText = roleData.description
        }
        return data;
    }

    const roles = getRoleData(orgRole);

    useEffect(() => {
        const getUserData = async () => {
            // i18n.changeLanguage(organization.platformLanguage)
            const user = await getAzureUser(context);
            setUserAzureData(user);
        };
        if (context.inProgress === "none") {
            getUserData();
        }
    }, [context]);

    // refs
    const inviteUserButton = useRef(null);// Create a reference to the inviteUserButton
    const emailInput = useRef(null); // Create a reference to the email input field

    const [selectedRole, setSelectedRole] = useState(toUpdateUser ? roles.find(role => role.roleType === toUpdateUser.roleType) : roles[1]); // Initialize selectedRole state variable with the first role from the roles prop
    const [invitedUser, setInvitedUser] = useState({}); // Initialize the invitedUser state variable to an empty object
    const [loading, setLoading] = useState(false); // Initialize the loading state variable to false
    const [errors, setErrors] = useState([]); // Initialize the errors state variable to an empty array

    // Function to render the dropdown list options with the roleType and roleDescription fields
    const itemRender = (li, itemProps) => {
        const item = itemProps.dataItem;
        const itemChildren = (
            <div
                className="w-100 mt-2 font-size-1"
            >
                {item.roleTypeText}
                <p className="text-muted mb-2 font-size-1">{item.roleDescriptionText}</p>
            </div>
        );
        return React.cloneElement(li, li.props, itemChildren);
    };

    // This function takes an array of emails as an argument and maps a validateEmail function to each email. 
    //It then filters out any errors that are not empty strings and sets the invitedUser state to the emails array and the errors state to the errors array.
    const handleUserEmail = emails => {
        const errors = emails.map(validateEmail).filter(error => error !== '');
        setInvitedUser(emails);
        setErrors(errors);
    };

    //This code handles the addition of an email address to an input field. 
    //It first trims and lowercases the email address, then validates it using the validateEmail function. 
    //If there is no error, it adds the email to the input field, otherwise it sets an error message.
    const handleEmailAdded = email => {
        email = email.trim().toLowerCase();
        const error = validateEmail(email);
        if (error === '') {
            emailInput.current.addItem(email);
        } else {
            setErrors([error]);
        }
    };

    // This function validates an email address to make sure it is a valid member of the organization. 
    // If the organization ID matches the environment variable, it checks to make sure the email is from a modernrequirements.com or codelessone.com domain. 
    // If either of these conditions are not met, it returns an error message.
    const validateEmail = email => {
        if (!isEmailValid(email)) {
            return `One of the email is Invalid`
        }
        if (isUserMemberOfOrganisation(email)) {
            return `${email} already exists in this workspace.`;
        }
        if (organization._id === process.env.REACT_APP_LIBRARY_ORGANIZATION_ID && !isInternalEmail(email)) {
            return `Must have modernrequirements.com or codelessone.com domain`;
        }
        return '';
    };

    //This code checks if a given email is a member of an organization by comparing it to the list of users in the organization.
    const isUserMemberOfOrganisation = email => organizationUsers.findIndex(user => user.email.toLowerCase() === email.toLowerCase()) > -1;

    // This function checks if an email address is internal or not.
    const isInternalEmail = email => email.split('@')[1].match(/modernrequirements.com|codelessone.com/i);

    // check wheather email is valid or not with regex
    const isEmailValid = email => {
        const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1, 3}\.[0-9]{1, 3}\.[0-9]{1, 3}\.[0-9]{1, 3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return emailRegex.test(String(email).toLowerCase());
    }

    //This function checks if the form has any errors. It returns true if the invitedUser is defined and has a length greater than 0, and all errors are empty strings. 
    //Otherwise, it returns false.
    const formHasErrors = () => {
        if (toUpdateUser) {
            if (toUpdateUser.roleType !== selectedRole.roleType) {
                return true
            } else {
                return false
            }
        } else {
            return invitedUser && invitedUser.length > 0 && errors.every(err => err === "")
        }
    };

    // This function is called when the user clicks the "Invite" button.
    const onInvite = async (event) => {
        event.preventDefault();
        const isFormValid = formHasErrors(); // Check if the form has any errors
        if (!isFormValid) return; // If the form has errors, return (do nothing

        // Set the loading state to true
        setLoading(true);
        inviteUserButton.current.disabled = true;

        try {
            let users = await inviteUsers(invitedUser)
            if (!showInIframe) {
                // order by users as same as invitedUser sequence
                users = invitedUser.map(email => users.find(user => user.email === email));
                // Add the new users to the existing users
                const updatedUsers = [...organizationUsers, ...users]
                // filter out duplicate users
                const uniqueUsers = updatedUsers.filter((user, index, self) => { return self.findIndex(user2 => user2.email === user.email) === index; });
                updateUsers(uniqueUsers);  // Call the updateUsers function (passed in as a prop
            }
            let message = users.length > 1 ? `${users.length} People have been invited` : `${users[0].email} has been invited`;   // Ternary operator used for string interpolation  
            showNotification(message); // Call the showNotification function (passed in as a prop)
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
            closeDialog(); // Close the dialog
        }
    };

    //This function is used to invite users to an organization. It takes in an array of users as an argument.
    const inviteUsers = async (users) => {
        // Call the API to invite users to an organization
        let invitedUsers = await inviteUserToOrganization(organization._id, selectedRole._id, users);

        // Send email to each user
        for (let user of invitedUsers) {
            // create properties object for email
            let properties = {
                userName: userAzureData.name,
                organizationName: organization.orgName,
                workspaceUrl: `${process.env.REACT_APP_RUNTIME_URL}/${organization.orgUrl}`,
                subject: "You have been invited to CodelessONE",
            }
             // generate workspace url based on user's azure id
             if (user.azureId && user.azureId !== "") {
                properties.designerUrl = properties.workspaceUrl
            } else {
                properties.designerUrl = `${process.env.REACT_APP_DESIGNER_URL}/signup`
            }
            // send email to invited user
            await sendEmail(user.email, process.env.REACT_APP_SENDGRID_INVITE_KEY, properties);
        }
        // Return the users
        return invitedUsers || [];
    };

    //This function is called when the user clicks the "Update" button.
    const onUpdate = async (event) => {
        event.preventDefault(); // Prevent the default action of the button
        inviteUserButton.current.disabled = true; // Disable the update button
        const isFormValid = formHasErrors(); // Check if the form has any errors
        if (!isFormValid) return; // If the form has errors, return (do nothing)

        setLoading(true); // Set the loading state to true
        try {
            const response = await changeUserOrganizationRole(organization._id, selectedRole._id, toUpdateUser._id); // Call the API to update the user's role in the organization
            if (response.status === "Success") {
                const role = roles.find(role => role._id === selectedRole._id); // Find the role in the roles array that matches the roleId in the response
                const index = organizationUsers.findIndex(user => user._id === toUpdateUser._id); // Find the index of the user in the organizationUsers array
                organizationUsers[index].roleType = role.roleType; // Update the roleType of the user
                updateUsers(organizationUsers); // Call the updateUsers function (passed in as a prop)
            }
        } catch (error) {  // Catch any errors that occur in the try block 
            console.log(error);  // Log the error to the console for debugging purposes 
        } finally {  // This code will always run regardless of whether or not an error occurs in the try block 
            setLoading(false);   // Set loading to false so the page can be updated with new data 
            closeDialog();   // Close the dialog box when finished 
        }
    }

    return (
        <Fragment>
            {loading && <OverlayLoader />}
            <div className={showInIframe ? 'py-4 mx-3 row' : 'pt-4 row'}>
                <div className="col-md-12 mb-3 font-size-1">
                    <div className="form-group">
                        <div className='mb-2'>
                            <label htmlFor="" required>
                                {toUpdateUser ?
                                    t("user_email_field_label")
                                    :
                                    t("invite_user_field_label")
                                }
                            </label>
                            {errors.map(err => (
                                <span className="text-danger d-inline-block ms-2">{err}</span>
                            ))}
                        </div>
                        {
                            toUpdateUser ?
                                <div className="disabled-input-wrapper">
                                    <input
                                        className="form-control"
                                        disabled={true}
                                        value={toUpdateUser.email}
                                    />
                                </div>
                                :
                                <MultiSelectInput
                                    ref={emailInput}
                                    isInviting={true}
                                    collectionUpdated={(e) => setInvitedUser(e)}
                                    sendError={(e) => setErrors([e])}
                                    onKeyPressed={() => setErrors([])}
                                    onItemAdded={(e) => handleEmailAdded(e)}
                                    onChanged={(e) => handleUserEmail(e)}
                                    placeholder={t("email_placeholder")}
                                ></MultiSelectInput>
                        }
                    </div>
                </div>
                <div className="col-md-12 mb-3 font-size-1">
                    <div className="form-group drpDwnTxt">
                        <label htmlFor="" className="mb-2">
                            {t("workspace_role_field_label")}
                        </label>
                        <DropDownList
                            onChange={(event) => setSelectedRole(event.value)} // Set the selectedRole state variable when the user selects a role from the dropdown list
                            data={roles} // Pass the roles prop as data to the dropdown list
                            itemRender={itemRender}
                            textField="roleTypeText" // Use the "roleType" field as the display text for the dropdown list options
                            dataItemKey="_id" // Use the "value" field as the key for the dropdown list options
                            value={selectedRole} // Set the default selected value to the selectedRole state variable
                        />
                    </div>
                </div>
                <div className="d-flex justify-content-end pt-3">
                    {/* Display a button to submit the form */}
                    {loading && (
                        <div className="d-inline-block">
                            <InnerLoader borderColor="green" />
                            <span className="me-4 text-success"></span>
                        </div>
                    )}
                    <button ref={inviteUserButton} className="btn btn-primary" disabled={!formHasErrors()} type={"button"} onClick={toUpdateUser ? onUpdate : onInvite}>
                        {toUpdateUser ?
                            t("buttons.update_button_label")
                            :
                            t("buttons.invite_button_label")
                        }
                    </button>
                </div>
            </div>
        </Fragment>
    );
};

// Component for the dialog that shows the invite form
export const InviteOrganizationUsersDailog = (props) => {

    const { t, i18n } = useTranslation([
        "users/invite_user_dialog"
    ], {
        useSuspense: false
    }) // Translation

    useEffect(() => {
        // i18n.changeLanguage(props.organization.platformLanguage)
    }, []);

    return (
        <Dialog
            width={'50%'}
            title={props.toUpdateUser ?
                t("update_user_dialog_title")
                :
                t("invite_user_dialog_title")
            }
            onClose={props.closeDialog}>
            {/* Render the InviteUsertoOrganizationForm component inside the dialog */}
            <InviteUsertoOrganizationForm {...props} />
        </Dialog>
    );
};